// 37_VOICE_TO_TEXT (doc 37/01 §3.1) -- the Student's microphone, recorded in
// a container the speech-to-text API takes. Pure browser code: no network, no
// store. Every way out of a recording (stop, cancel, an error, the device
// going away, the page leaving) releases the microphone -- each track is
// stopped exactly once.
//
// The API names the CONTAINER, not the codec (speech-to-text-api.md §1): a
// recorder's `audio/webm;codecs=opus` is `webm`. The first type this browser
// records that the API takes wins (D-9); the upload's `fileFormat` is then
// read from what the recorder really produced.

export const VOICE_MAX_SECONDS = 60
export const VOICE_MAX_BYTES = 5 * 1024 * 1024
export const VOICE_BITS_PER_SECOND = 32000

export const RECORDING_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
]

/**
 * The upload format for a recorder MIME type, or null.
 * @param {string} mimeType
 * @returns {'webm'|'ogg'|'m4a'|null}
 */
export function fileFormatForMimeType(mimeType) {
  const type = String(mimeType || '')
    .split(';')[0]
    .trim()
    .toLowerCase()
  if (type === 'audio/webm') return 'webm'
  if (type === 'audio/ogg') return 'ogg'
  if (type === 'audio/mp4' || type === 'audio/x-m4a') return 'm4a'
  return null
}

function recorderClass(env) {
  return env && typeof env.MediaRecorder === 'function'
    ? env.MediaRecorder
    : null
}

/**
 * The MIME type to ask the recorder for, or null when this browser records
 * none of them.
 */
export function getRecordingType(env = window) {
  const Recorder = recorderClass(env)
  if (!Recorder || typeof Recorder.isTypeSupported !== 'function') return null
  return (
    RECORDING_TYPES.find(type => {
      try {
        return Recorder.isTypeSupported(type)
      } catch (err) {
        return false
      }
    }) || null
  )
}

export function isVoiceCaptureSupported(env = window) {
  if (!env) return false
  // getUserMedia exists only on https or localhost pages.
  if (env.isSecureContext === false) return false
  const devices = env.navigator && env.navigator.mediaDevices
  if (!devices || typeof devices.getUserMedia !== 'function') return false
  return !!getRecordingType(env)
}

function failure(reason) {
  return { reason }
}

function reasonForMediaError(err) {
  const name = err && err.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'no_device'
  }
  if (name === 'NotReadableError' || name === 'AbortError') return 'busy'
  return 'unsupported'
}

/**
 * Opens the microphone and starts recording.
 *
 * Resolves to a handle: `stop()` -> Promise<{blob, fileFormat, durationMs}>,
 * `cancel()`, `onInterrupted(callback)`. Rejects with `{reason}`:
 * 'unsupported' | 'denied' | 'no_device' | 'busy'. `stop()` rejects with
 * `{reason}`: 'empty' | 'too_large' | 'unsupported' | 'interrupted'.
 */
export async function startRecording(env = window, { now = Date.now } = {}) {
  if (!isVoiceCaptureSupported(env)) throw failure('unsupported')
  const Recorder = recorderClass(env)
  const requestedType = getRecordingType(env)

  let stream
  try {
    stream = await env.navigator.mediaDevices.getUserMedia({ audio: true })
  } catch (err) {
    throw failure(reasonForMediaError(err))
  }

  const tracks = (stream && typeof stream.getTracks === 'function'
    ? stream.getTracks()
    : []
  ).slice()
  let released = false
  function release() {
    if (released) return
    released = true
    tracks.forEach(track => {
      try {
        track.stop()
      } catch (err) {
        // A track that cannot be stopped is already gone.
      }
    })
  }

  let recorder
  try {
    recorder = new Recorder(stream, {
      mimeType: requestedType,
      audioBitsPerSecond: VOICE_BITS_PER_SECOND,
    })
  } catch (err) {
    release()
    throw failure('unsupported')
  }

  const chunks = []
  const startedAt = now()
  let settled = false
  let stopPromise = null
  let interruptedCallback = null
  let resolveStop = null
  let rejectStop = null

  function finish(result, error) {
    if (settled) return
    settled = true
    release()
    if (resolveStop) {
      if (error) rejectStop(error)
      else resolveStop(result)
    }
  }

  // The recording ended without the Student's stop: the device went away or
  // the recorder failed. Nothing is handed out.
  function interrupt() {
    if (settled) return
    finish(null, failure('interrupted'))
    if (interruptedCallback) interruptedCallback()
  }

  function assemble() {
    const actualType = recorder.mimeType || requestedType
    const fileFormat =
      fileFormatForMimeType(actualType) || fileFormatForMimeType(requestedType)
    if (!fileFormat) return { error: failure('unsupported') }
    const blob = new Blob(chunks, { type: actualType })
    if (!blob.size) return { error: failure('empty') }
    if (blob.size > VOICE_MAX_BYTES) return { error: failure('too_large') }
    return {
      result: { blob, fileFormat, durationMs: Math.max(0, now() - startedAt) },
    }
  }

  recorder.addEventListener('dataavailable', event => {
    if (event && event.data && event.data.size) chunks.push(event.data)
  })
  recorder.addEventListener('stop', () => {
    if (!stopPromise) {
      // The recorder stopped by itself (the device went away).
      interrupt()
      return
    }
    const { result, error } = assemble()
    finish(result, error)
  })
  recorder.addEventListener('error', () => {
    if (!stopPromise) interrupt()
    else finish(null, failure('interrupted'))
  })

  // A track that ends while recording (unplugged, revoked, taken by the OS)
  // interrupts the recording.
  tracks.forEach(track => {
    if (track && typeof track.addEventListener === 'function') {
      track.addEventListener('ended', () => {
        if (settled || stopPromise) return
        interrupt()
        try {
          if (recorder.state !== 'inactive') recorder.stop()
        } catch (err) {
          // ignored: the recorder is going away with its track
        }
      })
    }
  })

  try {
    recorder.start()
  } catch (err) {
    release()
    throw failure('unsupported')
  }

  return {
    fileFormat:
      fileFormatForMimeType(recorder.mimeType) ||
      fileFormatForMimeType(requestedType),
    // Idempotent: a repeated stop gets the same answer.
    stop() {
      if (stopPromise) return stopPromise
      stopPromise = new Promise((resolve, reject) => {
        resolveStop = resolve
        rejectStop = reject
      })
      if (settled) {
        // Already over (interrupted or cancelled): nothing to hand out.
        rejectStop(failure('interrupted'))
        return stopPromise
      }
      try {
        if (recorder.state !== 'inactive') recorder.stop()
        else {
          const { result, error } = assemble()
          finish(result, error)
        }
      } catch (err) {
        finish(null, failure('interrupted'))
      }
      return stopPromise
    },
    // Idempotent; the recording is discarded.
    cancel() {
      if (settled) return
      settled = true
      chunks.length = 0
      try {
        if (recorder.state !== 'inactive') recorder.stop()
      } catch (err) {
        // ignored: the microphone is released below either way
      }
      release()
      if (resolveStop) rejectStop(failure('cancelled'))
    },
    onInterrupted(callback) {
      interruptedCallback = typeof callback === 'function' ? callback : null
    },
    get released() {
      return released
    },
  }
}
